import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useRouter } from 'vl-core/useRouter';
import useHAR from 'vl-common/src/hooks/useHAR';
import { useSystemType } from 'vl-common/src/context/provider.app';
import { clearHARserviceWorker, harServiceWorkerIsRunning } from '../lib/manageHARserviceWorker';
import { AsyncAntdButtonProps } from '../components/AsyncComponents/AsyncAntdButton/AsyncAntdButton';

export type HarFunctionsType = {
  enterTestingMode: (file: string, userType: string) => void;
  exitTestingMode: () => Promise<void>;
  record: () => Promise<void>;
  dump: () => void;
  clear: () => void;
  inRecordingMode: boolean;
  setInRecordingMode: Dispatch<SetStateAction<boolean>>;
  inTestingMode: boolean;
  setInTestingMode: Dispatch<SetStateAction<boolean>>;
  testingModalOpen: boolean;
  setTestingModalOpen: Dispatch<SetStateAction<boolean>>;
  dumpModalOpen: boolean;
  setDumpModalOpen: Dispatch<SetStateAction<boolean>>;
  dumpedHAR: any;
  harIsRunning: boolean;
  clearButtonProps: { props: AsyncAntdButtonProps; text: string };
  recordButtonProps: { props: AsyncAntdButtonProps; text: string };
  dumpButtonProps: { props: AsyncAntdButtonProps; text: string };
  testingButtonProps: { props: AsyncAntdButtonProps; text: string };
  disableButtonProps: { props: AsyncAntdButtonProps; text: string };
};

export const useHARFunctions: (getDynamicConfig: any) => HarFunctionsType = (getDynamicConfig) => {
  const router = useRouter();
  const [type] = useSystemType();
  const { record, dump, dumpedHAR, clear, inTestingMode, setInTestingMode, inRecordingMode, setInRecordingMode } =
    useHAR(getDynamicConfig);

  const [testingModalOpen, setTestingModalOpen] = useState(false);
  const [dumpModalOpen, setDumpModalOpen] = useState(false);
  const [harIsRunning, setHarIsRunning] = useState(false);

  useEffect(() => {
    harServiceWorkerIsRunning(setHarIsRunning);
  }, []);

  const enterTestingMode = (file: string, userType: string) => {
    setTestingModalOpen(false);
    if (file) {
      sessionStorage.setItem('har', `${userType}/${file}`);
      if (type === 'admin') {
        router.push(`/account/login?har=har/${userType}/${file}`);
      } else {
        router.push(`/login/?har=har/${userType}/${file}`);
      }
      setInTestingMode(true);
    }
  };

  const exitTestingMode = async () => {
    setTestingModalOpen(false);
    setInTestingMode(false);
    sessionStorage.removeItem('har');
    sessionStorage.removeItem('mocked');
    Auth.harUserId = '';
    Auth.isInHARMode = false;
    if (type === 'admin') {
      window.location.assign('#/account/login');
    } else {
      window.location.assign('/login');
    }
    record().then();
  };

  const clearButtonProps: { props: AsyncAntdButtonProps; text: string } = {
    props: {
      onClick: () => {
        clear();
      },
      disabled: inRecordingMode || inTestingMode,
      size: 'small',
      'data-testid': 'clear-button'
    },
    text: 'Clear'
  };

  const recordButtonProps: { props: AsyncAntdButtonProps; text: string } = {
    props: {
      onClick: () => {
        if (!inRecordingMode) {
          record().then(() => {
            setInRecordingMode(true);
            sessionStorage.setItem('recording', 'true');
          });
        }
      },
      disabled: inTestingMode,
      size: 'small',
      'data-testid': 'record-button',
      style: inRecordingMode ? { background: 'green', color: 'white' } : {}
    },
    text: !inRecordingMode ? 'Record' : 'Recording...'
  };

  const dumpButtonProps: { props: AsyncAntdButtonProps; text: string } = {
    props: {
      onClick: () => {
        dump();
        setInRecordingMode(false);
        sessionStorage.removeItem('recording');
        setDumpModalOpen(true);
      },
      disabled: inTestingMode,
      size: 'small',
      'data-testid': 'dump-button'
    },
    text: 'Dump'
  };

  const testingButtonProps: { props: AsyncAntdButtonProps; text: string } = {
    props: {
      onClick: () => {
        setTestingModalOpen(true);
      },
      disabled: inRecordingMode,
      size: 'small',
      'data-testid': 'testing-mode-button',
      style: inTestingMode ? { background: '#008001', color: 'white' } : {}
    },
    text: 'Testing mode'
  };

  const disableButtonProps: { props: AsyncAntdButtonProps; text: string } = {
    props: { 'data-testid': 'remove-har-button', onClick: clearHARserviceWorker, size: 'small' },
    text: 'Disable HAR'
  };

  return {
    enterTestingMode,
    exitTestingMode,
    record,
    dump,
    clear,
    inRecordingMode,
    setInRecordingMode,
    inTestingMode,
    setInTestingMode,
    testingModalOpen,
    setTestingModalOpen,
    dumpModalOpen,
    setDumpModalOpen,
    dumpedHAR,
    harIsRunning,
    clearButtonProps,
    recordButtonProps,
    dumpButtonProps,
    testingButtonProps,
    disableButtonProps
  };
};
